import React, { useEffect, useState } from "react";
import {
  TextField,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  InputAdornment,
  IconButton,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { axiosInstance } from "../../../utils/tokenSetCookie";
import { toast } from "react-toastify";
import {
  UserDetails,
  UserProfileform,
} from "../../../types/types";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import defaultProfile from "../../../assets/CLINICAL VISUALS LOGO_ICON.svg";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const CUSTOM_UPLOAD_BASE_URL = import.meta.env.VITE_CUSTOM_UPLOAD_BASE_URL;
const VISUALIZE__URL = import.meta.env.VITE_VISUALIZE__URL;
const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<UserProfileform>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    currentUsername: "",
    newUsername: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // New states
  const [editMode, setEditMode] = useState<
    "profile" | "username" | "password" | null
  >(null);
  const [usernameVerified, setUsernameVerified] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [initialProfile, setInitialProfile] = useState("");
  const [showNewPassword, setNewPasswordShow] = useState<boolean>(false);
  const [showConfirmPassword, setConfirmPasswordShow] =
    useState<boolean>(false);
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(true);
  const isMobile = useMediaQuery("(max-width:820px)");

  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
  const MAX_IMAGE_SIZE_MB = 5;

  // Simulate a 3-second loading period
  useEffect(() => {
    if (loading === true) {
      const timer = setTimeout(() => {
        setLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
    if (verifyLoading === true) {
      const timer = setTimeout(() => {
        setVerifyLoading(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [loading, verifyLoading]);

  // Get the user details from the API
  useEffect(() => {
    // Fetch user details from the API
    const getUserDetails = async () => {
      try {
        const response = await axiosInstance.get(
          API_BASE_URL + "users/getuserdetails"
        );
        if (response.status >= 200 && response.status <= 300) {
          setUserDetails(response.data.data);
          setInitialProfile(response.data.data.userImage);
        }
      } catch (err) {
        console.log(err);
      }
    };
    getUserDetails(); // Passing the payload as an argument
  }, []);

  // userDetails set as formdata when component mounts
  useEffect(() => {
    if (userDetails) {
      setFormData({
        firstName: userDetails?.firstName,
        lastName: userDetails?.lastName,
        email: userDetails?.emailId,
        phoneNumber: userDetails?.phone,
        currentUsername: "",
        newUsername: "",
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    }
  }, [userDetails]);

  const validateImage = (file: File): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error("Only JPEG, PNG, WEBP, and GIF formats are allowed.");
        return resolve(false);
      }

      if (file.size > MAX_IMAGE_SIZE_MB * 1024 * 1024) {
        toast.error("Image is too large. Please upload a file under 5MB.");
        return resolve(false);
      }

      // Optional: you can check image dimensions here if needed
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => {
        toast.error("Invalid image file.");
        resolve(false);
      };
      img.src = URL.createObjectURL(file);
    });
  };

  // Function to convert Base64 to File object
  // function base64ToFile(base64String: string) {
  //   if (!base64String || typeof base64String !== "string") {
  //     console.error("Invalid Base64 string:", base64String);
  //     return null;
  //   }

  //   const match = base64String.match(/^data:(.*?);base64,(.*)$/);
  //   if (!match) {
  //     console.error("Base64 format error:", base64String);
  //     return null;
  //   }

  //   const mime = match[1]; // Extract MIME type
  //   const base64Data = match[2]; // Extract Base64 content

  //   try {
  //     const byteCharacters = atob(base64Data);
  //     const byteNumbers = new Array(byteCharacters.length);
  //     for (let i = 0; i < byteCharacters.length; i++) {
  //       byteNumbers[i] = byteCharacters.charCodeAt(i);
  //     }

  //     const byteArray = new Uint8Array(byteNumbers);
  //     const fileBlob = new Blob([byteArray], { type: mime });

  //     return new File([fileBlob], `file.${mime.split("/")[1]}`, { type: mime });
  //   } catch (error) {
  //     console.error("Error decoding Base64:", error);
  //     return null;
  //   }
  // }

  // Function to get user profile details
  const getUserProfileDetails = async () => {
    try {
      const response = await axiosInstance.get(
        API_BASE_URL + "users/getuserdetails"
      );
      if (response.status >= 200 && response.status <= 300) {
        setUserDetails(response.data.data);
        setInitialProfile(response.data.data.userImage);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Handle input change event for form
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Prevent input if only spaces
    if (value.trim() === "" && value !== "") return;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // User profile image upload function and image preview also call API
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isValid = await validateImage(file);
      if (isValid) {
        console.log("File is valid", isValid);
        //  Send the File object to the API
        const formData = new FormData();
        formData.append("file", file);
        try {
          const response = await axiosInstance.post(
            `${API_BASE_URL}users/upload-user-image`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data", //  Ensure this header
              },
            }
          );
          if (response.status >= 200 && response.status < 300) {
            setLoading(true);
            toast.success(t("upload_successful"), {
              autoClose: 1000,
            });
            getUserProfileDetails();
          } else {
            toast.error(response.data.message, {
              autoClose: 1000,
            });
          }
        } catch (error) {
          console.error("Upload Error:", error);
          toast.error(t("failed_to_upload_animation_please_try_again"), {
            autoClose: 1000,
          });
        }

      }
      // if (file) {
      //   const reader = new FileReader(); // Create a FileReader
      //   reader.onloadend = async () => {
      //     // Handle the onloadend event
      //     const readedImage = reader.result as string; // Get the Base64 string
      //     const profileImage = base64ToFile(readedImage); // Convert Base64 to File object
      //     // Ensure we got a valid File object
      //     if (!profileImage) {
      //       console.error("Failed to convert Base64 to File");
      //       toast.error("Invalid image format");
      //       return;
      //     }
      //     //  Send the File object to the API
      //     const formData = new FormData();
      //     formData.append("file", profileImage);
      //     try {
      //       const response = await axiosInstance.post(
      //         `${API_BASE_URL}users/upload-user-image`,
      //         formData,
      //         {
      //           headers: {
      //             "Content-Type": "multipart/form-data", //  Ensure this header
      //           },
      //         }
      //       );
      //       if (response.status >= 200 && response.status < 300) {
      //         setLoading(true);
      //         toast.success(t("upload_successful"), {
      //           autoClose: 1000,
      //         });
      //         getUserProfileDetails();
      //       } else {
      //         toast.error(response.data.message, {
      //           autoClose: 1000,
      //         });
      //       }
      //     } catch (error) {
      //       console.error("Upload Error:", error);
      //       toast.error(t("failed_to_upload_animation_please_try_again"), {
      //         autoClose: 1000,
      //       });
      //     }
      //   };
      //   reader.readAsDataURL(file);
      // }
    }
  };

  // Function to handle edit mode
  const handleEdit = (mode: "profile" | "username" | "password") => {
    setEditMode(mode); // Set the edit mode
    setUsernameVerified(false); // Reset username verification
    setPasswordVerified(false); // Reset password verification
    if (mode === "profile") {
      setDisableSaveButton(false);
    } else {
      setDisableSaveButton(true);
    }
  };

  const handleVerify = async (type: "username" | "password") => {
    // Here you would typically make an API call to verify the username or password
    if (type === "username") {
      // Handle username verification
      if (formData.currentUsername !== null) {
        const payload = {
          currentUsername: formData.currentUsername,
        };
        try {
          const response = await axiosInstance.put(
            API_BASE_URL + "users/update-username",
            payload
          );

          if (response.status >= 200 && response.status <= 300) {
            setVerifyLoading(true);
            setTimeout(() => {
              toast.success("Username Validated", {
                autoClose: 2000,
              });
            }, 2000);

            setDisableSaveButton(false);
            setTimeout(() => {
              setUsernameVerified(true);
            }, 2000); // Delay by 2 second
          } else {
            toast.error(response.data.message, {
              autoClose: 1000,
            });
          }
        } catch (err) {
          console.log("err", err);
          toast.error("Current Username is invalid", {
            autoClose: 1000,
          });
        }
      }
    } else {
      // Handle password verification
      if (formData.currentPassword !== null) {
        const payload = {
          currentPassword: formData.currentPassword,
        };
        try {
          const response = await axiosInstance.put(
            API_BASE_URL + "users/update-password",
            payload
          );

          if (response.status >= 200 && response.status <= 300) {
            setVerifyLoading(true);
            setTimeout(() => {
              toast.success("Password Validated", {
                autoClose: 2000,
              });
            }, 2000);
            setDisableSaveButton(false);
            setTimeout(() => {
              setPasswordVerified(true);
            }, 2000); // Delay by 2 second
          } else {
            toast.error(response.data.message, {
              autoClose: 1000,
            });
          }
        } catch (err) {
          console.log("err", err);
        }
      }
    }
  };

  // Function to handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically make an API call to update the user information

    if (
      editMode === "username" &&
      usernameVerified == true &&
      formData.newUsername.length < 5
    ) {
      toast.error("Enter a valid username with more than 4 characters", {
        autoClose: 1000,
      });
      return;
    }
    if (
      editMode === "password" &&
      passwordVerified == true &&
      formData.newPassword.length < 5
    ) {
      toast.error("Enter a valid password with more than 4 characters", {
        autoClose: 1000,
      });
      return;
    }

    // Only check changes if editMode is profile
    if (editMode === "profile") {
      const isProfileChanged =
        formData.firstName !== userDetails?.firstName ||
        formData.lastName !== userDetails?.lastName ||
        formData.email !== userDetails?.emailId ||
        formData.phoneNumber !== userDetails?.phone;

      const isAnyFieldEmpty =
        (formData.firstName !== userDetails?.firstName &&
          formData.firstName.trim() !== "") ||
        (formData.lastName !== userDetails?.lastName &&
          formData.lastName.trim() !== "") ||
        (formData.email !== userDetails?.emailId &&
          formData.email.trim() !== "") ||
        (formData.phoneNumber !== userDetails?.phone &&
          formData.phoneNumber.trim() !== "");
      if (!isProfileChanged) {
        toast.info("No changes detected in profile.", {
          autoClose: 1000,
        });
        return;
      }

      if (!isAnyFieldEmpty) {
        toast.info("Empty fields detected in profile.", {
          autoClose: 1000,
        });
        return;
      }
    }

    const payload =
      editMode !== "profile" // Check if editMode is "profile"
        ? editMode !== "username" // Check if editMode is "username"
          ? {
            currentPassword: formData.currentPassword,
            newPassword: formData.newPassword,
            confirmPassword: formData.confirmPassword,
          }
          : {
            currentUsername: formData.currentUsername,
            newUsername: formData.newUsername,
          }
        : {
          firstName: formData.firstName,
          lastName: formData.lastName,
          emailId: formData.email,
          phoneNumber: formData.phoneNumber,
        };
    const endPoint =
      editMode !== "profile" // Check if editMode is "profile"
        ? editMode !== "username" // Check if editMode is "username"
          ? "users/update-password"
          : "users/update-username"
        : "users/update-profile";


    //  Update user details from the API
    const updateUserDetails = async (payload: any) => {
      try {
        const response = await axiosInstance.put(
          API_BASE_URL + endPoint,
          payload
        );
        if (response.status >= 200 && response.status <= 300) {

          setTimeout(() => {
            toast.success(
              editMode !== "profile"
                ? editMode !== "username"
                  ? "Password Updated"
                  : "Username Updated"
                : "Profile Updated",
              {
                autoClose: 2000,
              }
            );
          }, 2000);

          setEditMode(null);
          getUserProfileDetails();
        }
      } catch (error) {
        console.error("Error fetching user data", error);
      }
    };
    updateUserDetails(payload);
  };

  //  Password Show Hide
  const togglePasswordVisibility = () => {
    setNewPasswordShow(!showNewPassword);
  };
  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordShow(!showConfirmPassword);
  };
  const renderEditForm = () => {
    switch (editMode) {
      case "profile":
        return (
          <>
            <Grid
              item
              xs={12}
              display="flex"
              flexDirection="column"
              alignItems="center"
              mb={2}
            >
              <Box
                sx={{
                  width: { xs: 120, sm: 150 },
                  height: { xs: 120, sm: 150 },
                  borderRadius: "50%",
                  overflow: "hidden",
                  border: "2px solid #fff",
                  mb: 2,
                  boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)",
                    borderRadius: "50%",
                  },
                }}
              >
                {initialProfile ? (
                  <img
                    src={CUSTOM_UPLOAD_BASE_URL + initialProfile}
                    alt="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <img
                    src={defaultProfile}
                    alt="Profile"
                    style={{
                      width: isMobile ? 100 : 150,
                      // height: isMobile ? 100 : 150,
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
              <Button variant="contained" component="label" className="addbtn">
                {t("upload_image")}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("first_name")}
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: "50px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("last_name")}
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: "50px",
                    alignItems: "center",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("email")}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: "50px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                disabled
                label={t("phone_number")}
                name="phone_number"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: "50px",
                  },
                }}
              />
            </Grid>
          </>
        );
      case "username":
        return (
          <>
            <Box
              sx={{
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label={t("current_username")}
                  name="currentUsername"
                  value={formData.currentUsername}
                  onChange={handleInputChange}
                  disabled={usernameVerified}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      height: "50px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={4} ml={2}>
                <Button
                  className="addbtn"
                  variant="contained"
                  onClick={() => handleVerify("username")}
                  disabled={usernameVerified}
                >
                  {verifyLoading ? (
                    <CircularProgress
                      sx={{
                        color: "#fff",
                      }}
                      size={20}
                    />
                  ) : (
                    t("verify")
                  )}
                </Button>
              </Grid>
            </Box>
            {usernameVerified && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t("newUsername")}
                  name="newUsername"
                  value={formData.newUsername}
                  onChange={handleInputChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      height: "50px",
                    },
                  }}
                />
              </Grid>
            )}
          </>
        );
      case "password":
        return (
          <>
            <Grid item xs={8}>
              <TextField
                fullWidth
                type="password"
                label={t("current_password")}
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                disabled={passwordVerified}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    height: "50px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                className="addbtn"
                variant="contained"
                onClick={() => handleVerify("password")}
                disabled={passwordVerified}
              >
                {verifyLoading ? (
                  <CircularProgress
                    sx={{
                      color: "#fff",
                    }}
                    size={20}
                  />
                ) : (
                  t("verify")
                )}
                {/* {t("verify")} */}
              </Button>
            </Grid>
            {passwordVerified && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type={showNewPassword ? "text" : "password"}
                    label={t("new_password")}
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={togglePasswordVisibility}
                            edge="end"
                          >
                            {showNewPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "50px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type={showConfirmPassword ? "text" : "password"}
                    label={t("confirm_password")}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={toggleConfirmPasswordVisibility}
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        height: "50px",
                      },
                    }}
                  />
                </Grid>
              </>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            transform: "scale(1.4)",
            zIndex: 9999,
            backgroundImage: `url(${VISUALIZE__URL}+"link.mp4")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "50px 50px", // Set image size to 50px width and height
            backgroundColor: "#fff",
            opacity: 0.5,
          }}
        ></Box>
      ) : (
        <>
          <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Grid container spacing={3}>
              {/* Profile Card - Left Side */}
              <Grid item xs={12} md={5} lg={4}>
                <Card
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    height: "100%",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    },
                    border: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <CardContent
                    sx={{
                      p: { xs: 2, sm: 3 },
                      "&:last-child": { pb: { xs: 2, sm: 3 } },
                    }}
                  >
                    {/* Profile Image Section */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 120, sm: 150 },
                          height: { xs: 120, sm: 150 },
                          borderRadius: "50%",
                          overflow: "hidden",
                          border: "2px solid #fff",
                          mb: 2,
                          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                          position: "relative",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            boxShadow: "inset 0 0 10px rgba(0,0,0,0.1)",
                            borderRadius: "50%",
                          },
                        }}
                      >
                        <img
                          src={
                            initialProfile
                              ? CUSTOM_UPLOAD_BASE_URL + initialProfile
                              : defaultProfile
                          }
                          alt="Profile"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Profile Details Section */}
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {t("user_profile")}
                        </Typography>
                        <Button
                          onClick={() => handleEdit("profile")}
                          sx={{
                            minWidth: "auto",
                            p: 1,
                            borderRadius: "50%",
                            color: "primary.main",
                            backgroundColor: "rgba(25, 118, 210, 0.04)",
                            "&:hover": {
                              backgroundColor: "primary.main",
                              color: "white",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <EditOutlinedIcon />
                        </Button>
                      </Box>

                      <Divider sx={{ mb: 3 }} />

                      {/* Profile Info Grid */}
                      <Grid container spacing={2} sx={{ mb: 4 }}>
                        {[
                          {
                            label: "first_name",
                            value: userDetails?.firstName,
                          },
                          { label: "last_name", value: userDetails?.lastName },
                          { label: "email", value: userDetails?.emailId },
                          { label: "phone", value: userDetails?.phone },
                          {
                            label: "user_role",
                            value: userDetails?.userAccessTagName,
                          },
                        ].map((item, index) => (
                          <React.Fragment key={index}>
                            <Grid item xs={5}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 500,
                                }}
                              >
                                {t(item.label)}
                              </Typography>
                            </Grid>
                            <Grid item xs={7}>
                              <Typography variant="body2">
                                {item.value || "-"}
                              </Typography>
                            </Grid>
                          </React.Fragment>
                        ))}
                      </Grid>

                      {/* Login Details Section */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          {t("login_details")}
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {/* Username and Password Section */}
                        {[
                          {
                            label: "username",
                            action: () => handleEdit("username"),
                          },
                          {
                            label: "password",
                            action: () => handleEdit("password"),
                          },
                        ].map((item, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              mb: 2,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 500,
                                  mb: 0.5,
                                }}
                              >
                                {t(item.label)}
                              </Typography>
                              <Typography variant="body2">••••••</Typography>
                            </Box>
                            <Button
                              onClick={item.action}
                              variant="contained"
                              size="small"
                              sx={{
                                minWidth: "auto",
                                p: 1,
                                borderRadius: "8px",
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Edit Forms - Right Side */}
              <Grid item xs={12} md={7} lg={8}>
                {editMode && (
                  <Card
                    sx={{
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                      mt: { xs: 2, md: 0 },
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                      },
                      border: "1px solid rgba(0,0,0,0.05)",
                    }}
                  >
                    <CardContent
                      sx={{
                        p: { xs: 2, sm: 3 },
                        "&:last-child": { pb: { xs: 2, sm: 3 } },
                      }}
                    >
                      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                        {editMode === "profile"
                          ? t("edit_profile")
                          : editMode === "username"
                            ? t("update_username")
                            : t("update_password")}
                      </Typography>

                      <form onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                          {renderEditForm()}
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                mt: 2,
                              }}
                            >
                              <Button
                                type="submit"
                                variant="contained"
                                disabled={disableSaveButton}
                                sx={{
                                  borderRadius: "8px",
                                  px: 4,
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                  "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 6px 15px rgba(0,0,0,0.15)",
                                  },
                                  "&:not(:disabled)": {
                                    transition: "all 0.2s ease",
                                  },
                                }}
                              >
                                {t("save")}
                              </Button>
                              <Button
                                onClick={() => setEditMode(null)}
                                variant="outlined"
                                sx={{
                                  borderRadius: "8px",
                                  px: 4,
                                  "&:hover": {
                                    transform: "translateY(-2px)",
                                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                                  },
                                  transition: "all 0.2s ease",
                                }}
                              >
                                {t("cancel")}
                              </Button>
                            </Box>
                          </Grid>
                        </Grid>
                      </form>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </>
  );
};

export default UserProfile;
